import { type PropsWithChildren, useCallback } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { useQueryClient } from '@tanstack/react-query';
import { WatchlistProvider } from '@pkgs/components/providers/WatchlistProvider';
import { type TrackingConfig } from '@pkgs/components/providers/AppContextProvider';
import { useStatusMessage } from '@ev-tech/ui-core-components/StatusMessageProvider';
import { alertVariants } from '@ev-tech/ui-core-components/Alert';

import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useSettings } from '@/hooks/useSettings';
import { useSearchApi } from '@/hooks/useSearchApi';

import { useDidomi } from './DidomiContextProvider';

export const WatchlistProviderWrapper = ({ children, tracking }: PropsWithChildren<{ tracking?: TrackingConfig }>) => {
  const { isSnowplowInitialized } = useDidomi();
  const queryClient = useQueryClient();
  const { language, countryCode } = useSettings();
  const searchApi = useSearchApi();
  const { t } = useTranslation(TRANSLATION_NAMESPACE.favorites);

  const { showMessage } = useStatusMessage();

  const onCreate = useCallback(
    (status: 'success' | 'error') => {
      showMessage(t(status === 'success' ? 'search.userAccount.favorites.added' : 'search.userAccount.favorites.added.failed'), {
        alertType: status === 'success' ? alertVariants.success : alertVariants.error,
      });
    },
    [showMessage, t],
  );

  const onDelete = useCallback(
    (status: 'success' | 'error') => {
      showMessage(t(status === 'success' ? 'search.userAccount.favorites.deleted' : 'search.userAccount.favorites.deleted.failed'), {
        alertType: status === 'success' ? alertVariants.success : alertVariants.error,
      });
    },
    [showMessage, t],
  );

  return (
    <WatchlistProvider
      onCreate={onCreate}
      onDelete={onDelete}
      queryClient={queryClient}
      language={language}
      api={searchApi}
      countryCode={countryCode}
      tracking={{ ...tracking, isSnowplowInitialized }}
    >
      {children}
    </WatchlistProvider>
  );
};
